import { computed, ref, watch, type ComputedRef, toRef, reactive } from "vue";
import { useMediaContainer } from "./useMediaContainer";
import { useMediaContainerLayer } from "./useMediaContainerLayer";

export interface UseMediaCardProps {
  // Базовые props
  mediaType?: "image" | "video";
  width?: string;
  height?: string;
  maxHeight?: string;
  minHeight?: string;
  aspectRatio?: number;
  borderRadius?: number;
  objectFit?: "cover" | "contain";

  // Props для медиа
  imageProps?: {
    containerWidth?: number | null;
    containerHeight?: number | string | null;
    containerPreset?: {
      width?: number | null;
      height?: number | string | null;
      aspectRatio?: number | null;
    };
  };
  videoProps?: {
    containerWidth?: number | null;
    containerHeight?: number | string | null;
    containerPreset?: {
      width?: number | null;
      height?: number | string | null;
      aspectRatio?: number | null;
    };
  };

  // Props для маски
  maskProps?: {
    shape?: string;
    enabled?: boolean;
    maskAspectRatio?: number;
  };
  hasMask?: boolean;
}

// Same ratios as ContainerSelectInline(Child).vue's getContainerDimensions/getPreviewStyle.
// Backward-compat fallback for lessons saved before containerPreset.aspectRatio was wired up
// for "auto × N" — without this, old content would fall through to the image's own natural
// aspect ratio instead of the ratio the preset was designed to have.
const AUTO_HEIGHT_PRESET_ASPECT_RATIOS: Record<number, number> = {
  50: 32 / 9,
  100: 21 / 7,
  200: 21 / 8,
  300: 21 / 9,
  400: 16 / 9,
  500: 3 / 2,
  600: 4 / 3,
};

/**
 * Централизованный composable для всех вычислений стилей MediaCard
 * Возвращает все необходимые стили и состояния для всех уровней:
 * 1. Container (MediaContainerLayer)
 * 2. Effects (MediaTransformLayer, MaskLayer)
 * 3. Content (MediaContentLayer)
 */
export function useMediaCard(props: UseMediaCardProps) {
  // ============================================
  // БАЗОВЫЕ ВЫЧИСЛЕНИЯ
  // ============================================

  const objectFit = computed(() => props.objectFit || "contain");
  const borderRadius = computed(() => {
    return props.borderRadius !== undefined && props.borderRadius !== null 
      ? props.borderRadius 
      : undefined;
  });

  const maskEnabled = computed(() => {
    const maskShape = props.maskProps?.shape || "square";
    const hasMask = !!maskShape && maskShape !== "square";
    return hasMask && (props.maskProps?.enabled ?? false);
  });

  // Використовуємо ref для форсування реактивності
  const containerPresetRef = ref<{ width?: number | null; height?: number | string | null; aspectRatio?: number | null } | null>(null);
  const containerWidthRef = ref<number | null>(null);
  const containerHeightRef = ref<number | string | null>(null);
  const containerAspectRatioRef = ref<number | undefined>(undefined);

  // Функція для оновлення значень з containerPreset
  const updateContainerFromPreset = () => {
    const preset = props.imageProps?.containerPreset ?? props.videoProps?.containerPreset;
    containerPresetRef.value = preset || null;

    if (preset) {
      containerWidthRef.value = preset.width !== undefined && preset.width !== null ? preset.width : null;
      containerHeightRef.value = preset.height !== undefined && preset.height !== null ? preset.height : null;
      containerAspectRatioRef.value = preset.aspectRatio !== undefined && preset.aspectRatio !== null ? preset.aspectRatio : undefined;
    } else {
      // Зворотна сумісність: читаємо з окремих властивостей
      containerWidthRef.value = props.imageProps?.containerWidth ?? props.videoProps?.containerWidth ?? null;
      containerHeightRef.value = props.imageProps?.containerHeight ?? props.videoProps?.containerHeight ?? null;
      containerAspectRatioRef.value = props.aspectRatio || undefined;
    }
  };

  // Ініціалізуємо значення
  updateContainerFromPreset();

  // Відстежуємо зміни в containerPreset через watch з deep: true
  watch(
    () => props.imageProps?.containerPreset,
    (newPreset) => {
      updateContainerFromPreset();
    },
    { deep: true, immediate: true }
  );

  watch(
    () => props.videoProps?.containerPreset,
    (newPreset) => {
      updateContainerFromPreset();
    },
    { deep: true, immediate: true }
  );

  // Також відстежуємо окремі властивості для зворотної сумісності
  watch(
    () => [props.imageProps?.containerWidth, props.videoProps?.containerWidth, props.imageProps?.containerHeight, props.videoProps?.containerHeight, props.aspectRatio],
    () => {
      // Оновлюємо тільки якщо немає containerPreset
      if (!props.imageProps?.containerPreset && !props.videoProps?.containerPreset) {
        updateContainerFromPreset();
      }
    },
    { immediate: true }
  );

  // Computed для доступу до значень
  const containerPreset = computed(() => containerPresetRef.value);
  const containerWidth = computed(() => containerWidthRef.value);
  const containerHeight = computed(() => containerHeightRef.value);

  // ============================================
  // ВЫЧИСЛЕНИЕ ASPECT RATIO МЕДИА
  // ============================================

  const { aspectRatio: aspectRatioFromFit } = useMediaContainerLayer(
    computed(() => props.mediaType),
    objectFit,
    computed(() => props.imageProps as any),
    computed(() => props.videoProps as any),
    computed(() => props.maskProps),
    computed(() => maskEnabled.value)
  );

  const contentAspectRatio = computed(() => aspectRatioFromFit.value);

  // ============================================
  // ВЫЧИСЛЕНИЕ СТИЛЕЙ ДЛЯ КОНТЕЙНЕРА
  // ============================================

  // Вспомогательная функция для преобразования значений в CSS
  function toCssValue(value: string | number | null | undefined): string | undefined {
    if (value === null || value === undefined) {
      return undefined;
    }
    if (typeof value === 'number') {
      return `${value}px`;
    }
    return value;
  }

  // Вычисляет финальные размеры с учетом aspectRatio
  function calculateFinalDimensions(
    width: number | null,
    height: number | string | null,
    aspectRatio: number | undefined
  ): { finalWidth: number | null; finalHeight: number | string | null } {
    let finalWidth = width;
    let finalHeight = height;

    if (aspectRatio && aspectRatio > 0) {
      const hasExplicitWidth = width !== null && width !== undefined;
      const hasExplicitHeight = height !== null && height !== undefined && typeof height === 'number';

      if (hasExplicitWidth && !hasExplicitHeight) {
        // Если есть только ширина, вычисляем высоту из aspectRatio (только для числовых значений)
        if (typeof height !== 'string') {
          finalHeight = width / aspectRatio;
        }
      } else if (hasExplicitHeight && !hasExplicitWidth) {
        // Если есть только высота, вычисляем ширину из aspectRatio
        finalWidth = height * aspectRatio;
      }
    }

    return { finalWidth, finalHeight };
  }

  // Устанавливает стили для ширины
  function applyWidthStyles(
    styles: Record<string, string | number>,
    finalWidth: number | null,
    effectiveAspectRatio: number | undefined
  ): void {
    // Container width is always 100% — all editor formats are "auto × N".
    // The stored containerWidth (from image dimensions) is never used as explicit px.
    styles.width = "100%";
  }

  // Устанавливает стили для высоты
  function applyHeightStyles(
    styles: Record<string, string | number>,
    finalHeight: number | string | null,
    effectiveAspectRatio: number | undefined,
    hasExplicitWidth: boolean
  ): void {
    if (finalHeight !== null && finalHeight !== undefined) {
      if (typeof finalHeight === 'string') {
        styles.height = finalHeight;
      } else if (!isNaN(finalHeight) && !effectiveAspectRatio) {
        // "auto × N" preset with an aspect ratio: let aspect-ratio govern height
        // at every container width instead of locking it to N px — otherwise the
        // crop framing (object-fit:cover) differs between desktop and mobile.
        styles.height = `${finalHeight}px`;
      }
    } else if (props.height) {
      styles.height = props.height;
    } else if (effectiveAspectRatio && effectiveAspectRatio > 0 && !hasExplicitWidth) {
      // aspect-ratio handles height
    }
  }

  // Применяет дополнительные стили (minHeight, maxHeight, borderRadius)
  function applyAdditionalStyles(
    styles: Record<string, string | number>,
    hasExplicitHeight: boolean,
    effectiveAspectRatio: number | undefined
  ): void {
    // Применяем minHeight если он задан
    const minHeightValue = toCssValue(props.minHeight);
    if (minHeightValue) {
      styles.minHeight = minHeightValue;
    }

    // maxHeight применяется только когда нет ни height, ни aspectRatio, ни явной высоты
    if (props.maxHeight && !hasExplicitHeight && !props.height && !effectiveAspectRatio) {
      const maxHeightValue = toCssValue(props.maxHeight);
      if (maxHeightValue) {
        styles.maxHeight = maxHeightValue;
      }
    }

    // Применяем borderRadius для обоих режимов (cover и contain)
    const borderRadiusValue = toCssValue(borderRadius.value);
    if (borderRadiusValue) {
      styles.borderRadius = borderRadiusValue;
    }
  }

  const containerStyles = computed(() => {
    const styles: Record<string, string | number> = {};

    const hasExplicitWidth = containerWidth.value !== null && containerWidth.value !== undefined;
    const hasExplicitHeight = containerHeight.value !== null && containerHeight.value !== undefined;

    // Raw preset aspectRatio only — must NOT fall back to props.aspectRatio here. props.aspectRatio
    // is the image's native ratio, passed unconditionally by Block*CardView; if merged in this early
    // it always wins over the "auto × N" height-based ratio below, so the format's own ratio
    // (1.5, 4/3, etc.) would never apply. The props.aspectRatio fallback happens further down instead.
    let effectiveAspectRatio: number | undefined = containerAspectRatioRef.value ?? undefined;

    const { finalWidth, finalHeight } = calculateFinalDimensions(
      containerWidth.value,
      containerHeight.value,
      effectiveAspectRatio
    );

    // Compute from container dimensions when both explicit
    if (!effectiveAspectRatio && finalWidth !== null && finalHeight !== null &&
        typeof finalWidth === 'number' && typeof finalHeight === 'number' &&
        !isNaN(finalWidth) && !isNaN(finalHeight) && finalHeight > 0) {
      effectiveAspectRatio = finalWidth / finalHeight;
    }

    // Backward-compat: content saved before containerPreset.aspectRatio was wired up for
    // "auto × N" only has the height number — look up the ratio that preset was meant to have.
    // This table was designed for un-masked "auto × N" formats (hero/background media) and
    // skews wide (e.g. 300 → 21/9). A masked photo (Block2's blob-shaped avatar, etc.) has no
    // relation to that legacy format — applying it here squashes the mask into a wide strip
    // and, combined with object-fit:cover, crops large portions off the top/bottom of the
    // photo. So masked media skips this lookup entirely and falls through to its own ratio below.
    if (!effectiveAspectRatio && !maskEnabled.value && !hasExplicitWidth && typeof containerHeight.value === 'number') {
      effectiveAspectRatio = AUTO_HEIGHT_PRESET_ASPECT_RATIOS[containerHeight.value];
    }

    // Callers without a sized "auto × N" preset (e.g. Block2CardView) pass their own aspectRatio
    // prop directly — honor it here, below the preset-driven lookups above.
    if (!effectiveAspectRatio) {
      effectiveAspectRatio = props.aspectRatio ?? undefined;
    }

    // Fallback: image's natural aspect ratio (always stored in imageProps).
    // Used only for "auto × auto" / "auto × full" — the sized "auto × N" presets
    // now carry their own aspectRatio from ContainerSelectInline(Child).
    if (!effectiveAspectRatio) {
      const ir = (props.imageProps as any)?.aspectRatio;
      const iw = (props.imageProps as any)?.width;
      const ih = (props.imageProps as any)?.height;
      const vr = (props.videoProps as any)?.aspectRatio;
      const vw = (props.videoProps as any)?.width;
      const vh = (props.videoProps as any)?.height;
      if (ir && ir > 0) effectiveAspectRatio = ir;
      else if (iw && ih && ih > 0) effectiveAspectRatio = iw / ih;
      else if (vr && vr > 0) effectiveAspectRatio = vr;
      else if (vw && vh && vh > 0) effectiveAspectRatio = vw / vh;
    }

    // Last resort only: nothing is known about the photo itself (no stored ratio, no
    // natural dimensions) — fall back to the mask's own shape rather than the legacy
    // widescreen table. This must stay LOWEST priority: the mask PNG's own ratio (here
    // ~1.04, near-square) has no relation to any given photo's real framing — preferring
    // it over the photo's actual ratio is what crops portrait photos at top/bottom.
    if (!effectiveAspectRatio && maskEnabled.value && props.maskProps?.maskAspectRatio) {
      effectiveAspectRatio = props.maskProps.maskAspectRatio;
    }

    applyWidthStyles(styles, finalWidth, effectiveAspectRatio);
    // When an aspect ratio is known, height is derived from it via CSS aspect-ratio
    // at every container width — same crop framing on desktop and mobile. Only formats
    // without an aspect ratio (e.g. legacy stored px height) get an explicit height.
    applyHeightStyles(styles, finalHeight, effectiveAspectRatio, hasExplicitWidth);

    // Always include aspect-ratio — governs height directly when no explicit height
    // was set above, and lets the @container fallback in MediaContainerLayer/MaskLayer
    // still work for the legacy explicit-height formats.
    if (effectiveAspectRatio && effectiveAspectRatio > 0) {
      styles.aspectRatio = effectiveAspectRatio;
    }

    applyAdditionalStyles(styles, hasExplicitHeight, effectiveAspectRatio);

    return styles;
  });

  // ============================================
  // ВЫЧИСЛЕНИЕ СТИЛЕЙ ДЛЯ ЭФФЕКТОВ (MediaTransformLayer)
  // ============================================

  const effectsStyles = computed(() => {
    const styles: Record<string, string | number> = {
      ...containerStyles.value,
    };

    // В режиме cover применяем height: 100% для эффектов
    if (objectFit.value === 'cover') {
      styles.height = '100%';
    }

    return styles;
  });

  // ============================================
  // ВЫЧИСЛЕНИЕ СТИЛЕЙ ДЛЯ КОНТЕНТА (MediaContentLayer)
  // ============================================

  const contentStyles = computed(() => {
    const styles: Record<string, string | number> = {};

    if (objectFit.value === 'cover') {
      // В режиме cover применяем width: 100% и height: 100%
      styles.width = '100%';
      styles.height = '100%';
    } else {
      // В режиме contain используем значения из containerStyles
      if (containerStyles.value.width) {
        styles.width = containerStyles.value.width;
      }
      if (containerStyles.value.maxWidth) {
        styles.maxWidth = containerStyles.value.maxWidth;
      }
      if (containerStyles.value.height) {
        styles.height = containerStyles.value.height;
      }
      if (containerStyles.value.aspectRatio) {
        styles.aspectRatio = containerStyles.value.aspectRatio;
      }
    }

    // Применяем object-fit ко всем элементам
    if (objectFit.value) {
      styles.objectFit = objectFit.value;
    }

    // Применяем borderRadius
    const borderRadiusValue = toCssValue(borderRadius.value);
    if (borderRadiusValue) {
      styles.borderRadius = borderRadiusValue;
    }

    return styles;
  });

  // ============================================
  // ВЫЧИСЛЕНИЕ СТИЛЕЙ ДЛЯ МАСКИ (MaskLayer)
  // ============================================

  const maskStyles = computed(() => {
    const styles: Record<string, string | number> = {};

    if (objectFit.value === 'contain') {
      // В режиме contain маска должна быть вписана в контейнер
      if (containerStyles.value.width) {
        styles.width = containerStyles.value.width;
      } else {
        styles.width = "100%";
      }
      if (containerStyles.value.height) {
        styles.height = containerStyles.value.height;
      }
      if (containerStyles.value.aspectRatio) {
        styles.aspectRatio = containerStyles.value.aspectRatio;
      } else if (!maskEnabled.value && contentAspectRatio.value) {
        // Если маски нет, используем aspectRatio контента
        styles.aspectRatio = contentAspectRatio.value;
      }
    } else if (objectFit.value === 'cover') {
      // В режиме cover маска заполняет контейнер.
      // height:100% — but parent now uses aspect-ratio (no explicit height),
      // so 100% resolves correctly from the aspect-ratio-computed height.
      styles.width = "100%";
      styles.height = "100%";
      if (containerStyles.value.aspectRatio) {
        styles.aspectRatio = containerStyles.value.aspectRatio;
      }
      if (containerStyles.value.maxHeight) {
        styles.maxHeight = containerStyles.value.maxHeight;
      }
    }

    // Применяем maxHeight и minHeight если они заданы
    if (props.maxHeight) {
      styles.maxHeight = props.maxHeight;
    }
    if (props.minHeight) {
      styles.minHeight = props.minHeight;
    }

    return styles;
  });

  // ============================================
  // ВЫЧИСЛЕНИЕ ДОПОЛНИТЕЛЬНЫХ СТИЛЕЙ ДЛЯ КОНТЕЙНЕРА
  // (с учетом minHeight и maxHeight)
  // ============================================

  const containerStylesWithLimits = computed(() => {
    const styles: Record<string, string | number> = {
      ...containerStyles.value,
    };

    // Применяем minHeight и maxHeight если они заданы
    if (props.minHeight) {
      styles.minHeight = props.minHeight;
    }
    if (props.maxHeight) {
      styles.maxHeight = props.maxHeight;
    }

    return styles;
  });

  // ============================================
  // ВЫЧИСЛЕНИЕ СОСТОЯНИЙ ДЛЯ useMediaContainer
  // ============================================

  const containerWidthForUseMediaContainer = computed(() => {
    const width = containerStyles.value.width;
    return typeof width === 'string' ? width : "100%";
  });

  const containerHeightForUseMediaContainer = computed(() => {
    const height = containerStyles.value.height;
    return typeof height === 'string' ? height : undefined;
  });

  const {
    maskAspectRatio,
    isContain,
    isCover,
    hasMask: hasMaskFromContainer,
    hasNoMask,
  } = useMediaContainer({
    width: containerWidthForUseMediaContainer,
    height: containerHeightForUseMediaContainer,
    objectFit: objectFit,
    aspectRatio: contentAspectRatio,
    maskProps: props.maskProps,
    hasMask: computed(() => maskEnabled.value),
  });

  // ============================================
  // ВОЗВРАТ ВСЕХ ВЫЧИСЛЕНИЙ
  // ============================================

  return {
    // Стили для разных уровней
    containerStyles: containerStylesWithLimits,
    effectsStyles,
    contentStyles,
    maskStyles,

    // Базовые вычисления
    objectFit,
    borderRadius,
    maskEnabled,

    // Aspect ratios
    contentAspectRatio,
    maskAspectRatio,
    containerAspectRatio: computed(() => {
      const aspectRatio = containerStyles.value.aspectRatio;
      return typeof aspectRatio === 'number' ? aspectRatio : undefined;
    }),

    // Размеры контейнера
    containerWidth: containerWidthForUseMediaContainer,
    containerHeight: containerHeightForUseMediaContainer,
    
    // Container preset для відлагодження
    containerPreset,

    // Состояния
    isContain,
    isCover,
    hasMask: hasMaskFromContainer,
    hasNoMask,
  };
}

